import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { InstallAppPrompt } from "./components/InstallAppPrompt";

// Layout
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AgentsPage from "./pages/AgentsPage";
import AgentProfilePage from "./pages/AgentProfilePage";
import TopPerformersPage from "./pages/TopPerformersPage";
import DashboardPage from "./pages/DashboardPage";
import PricingPage from "./pages/PricingPage";
import AdminPage from "./pages/AdminPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Layout wrapper
const Layout = ({ children, showFooter = true }) => {
    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">{children}</main>
            {showFooter && <Footer />}
        </div>
    );
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/"
                element={
                    <Layout>
                        <HomePage />
                    </Layout>
                }
            />
            <Route
                path="/login"
                element={
                    <Layout showFooter={false}>
                        <LoginPage />
                    </Layout>
                }
            />
            <Route
                path="/register"
                element={
                    <Layout showFooter={false}>
                        <RegisterPage />
                    </Layout>
                }
            />
            <Route
                path="/agents"
                element={
                    <Layout>
                        <AgentsPage />
                    </Layout>
                }
            />
            <Route
                path="/agents/:id"
                element={
                    <Layout>
                        <AgentProfilePage />
                    </Layout>
                }
            />
            <Route
                path="/top-performers"
                element={
                    <Layout>
                        <TopPerformersPage />
                    </Layout>
                }
            />
            <Route
                path="/pricing"
                element={
                    <Layout>
                        <PricingPage />
                    </Layout>
                }
            />

            {/* Protected Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DashboardPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dashboard/*"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DashboardPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <AdminPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                    <InstallAppPrompt />
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
